/* eslint-disable spellcheck/spell-checker */
import { ENVS, getEnv } from '@adsk/acs-sc-web-platform-api';

import { registerAddEventModifier } from '../utils/dynatrace/registerAddEventModifier';
import {
  isDynatraceDisabled,
  isDynatraceOverrideAllowed,
  persistDynatraceOverride,
} from '../utils/getDynatraceOverride';

// When pasting a Dynatrace snippet, keep in sync: `data-dtconfig` + `data-config`
// (agentUri/featureHash), and `xb` + `data-appconfig.requestExclusions` (domains).
export const SCRIPT_ATTRIBUTES_PER_ENV: Record<
  keyof typeof ENVS,
  Record<string, string>
> = {
  [ENVS.QA]: {
    'data-dtconfig':
      'app=ef522bfead835200|cors=1|owasp=1|featureHash=ICA15789NPQRTUVXfqrux|msl=153600|xb=bugsnag^bs.com^pdatadoghq^bs.com|reportUrl=https://bf72614kga.bf.dynatrace.com/bf|rdnt=1|uxrgce=1|cuc=sopq8vl9|srms=2,1,0,0%2Ftextarea%2Cinput%2Cselect%2Coption;0%2Fdatalist;0%2Fform%20button;0%2F%5Bdata-dtrum-input%5D;0%2F.data-dtrum-input;1%2F%5Edata%28%28%5C-.%2B%24%29%7C%24%29|mel=100000|expw=1|lastModification=1784496524090|tp=500,50,0|srbbv=2|agentUri=https://js-cdn.dynatrace.com/jstag/1672bab6729/ruxitagent_ICA15789NPQRTUVXfqrux_10341260622154106.js',
    'data-config':
      '{"revision":1784496524090,"beaconUri":"https://bf72614kga.bf.dynatrace.com/bf","agentUri":"https://js-cdn.dynatrace.com/jstag/1672bab6729/ruxitagent_ICA15789NPQRTUVXfqrux_10341260622154106.js","environmentId":"sopq8vl9","modules":"ICA15789NPQRTUVXfqrux"}',
    'data-envconfig': '{"tracestateKeyPrefix":"558cacab-9d39b7de"}',
    'data-appconfig':
      '{"app":"ef522bfead835200","enableMetadataCapturing":false,"requestExclusions":"bugsnag\\\\.com|datadoghq\\\\.com"}',
  },
  [ENVS.STAGING]: {
    'data-dtconfig':
      'app=a78d201e47294161|auto=1|cors=1|owasp=1|featureHash=ICA15789NPQRTUVXfqrux|msl=153600|xb=datadoghq^bs.com^pbugsnag^bs.com|reportUrl=https://bf24247twu.bf.dynatrace.com/bf|rdnt=1|uxrgce=1|cuc=sw6sw95f|srms=2,1,0,0%2Ftextarea%2Cinput%2Cselect%2Coption;0%2Fdatalist;0%2Fform%20button;0%2F%5Bdata-dtrum-input%5D;0%2F.data-dtrum-input;1%2F%5Edata%28%28%5C-.%2B%24%29%7C%24%29|mel=100000|expw=1|lastModification=1783935978068|tp=500,50,0|srbbv=2|agentUri=https://js-cdn.dynatrace.com/jstag/1672bab6729/ruxitagent_ICA15789NPQRTUVXfqrux_10341260622154106.js',
    'data-config':
      '{"revision":1783935978068,"beaconUri":"https://bf24247twu.bf.dynatrace.com/bf","agentUri":"https://js-cdn.dynatrace.com/jstag/1672bab6729/ruxitagent_ICA15789NPQRTUVXfqrux_10341260622154106.js","environmentId":"sw6sw95f","modules":"ICA15789NPQRTUVXfqrux"}',
    'data-envconfig': '{"tracestateKeyPrefix":"6eb0c3d-9d39b7de"}',
    'data-appconfig':
      '{"app":"a78d201e47294161","enableMetadataCapturing":false,"requestExclusions":"datadoghq\\.com|bugsnag\\.com"}',
  },
  [ENVS.PRODUCTION]: {
    'data-dtconfig':
      'app=2c9efb0d298e29ed|auto=1|cors=1|owasp=1|featureHash=ICA15789NPQRTUVXfqrux|msl=153600|srsr=50000|xb=bugsnag^bs.com^pdatadoghq^bs.com|reportUrl=https://bf74291tsy.bf.dynatrace.com/bf|rdnt=1|uxrgce=1|cuc=rieos4aw|srms=2,2,1,|mel=100000|expw=1|lastModification=1784070747285|tp=500,50,0|srbbv=2|agentUri=https://js-cdn.dynatrace.com/jstag/1672bab6729/ruxitagent_ICA15789NPQRTUVXfqrux_10341260622154106.js',
    'data-config':
      '{"revision":1784070747285,"beaconUri":"https://bf74291tsy.bf.dynatrace.com/bf","agentUri":"https://js-cdn.dynatrace.com/jstag/1672bab6729/ruxitagent_ICA15789NPQRTUVXfqrux_10341260622154106.js","environmentId":"rieos4aw","modules":"ICA15789NPQRTUVXfqrux"}',
    'data-envconfig': '{"tracestateKeyPrefix":"bed31526-9d39b7de"}',
    'data-appconfig':
      '{"app":"2c9efb0d298e29ed","userInteractionCapture":["all"],"enableMetadataCapturing":false,"requestExclusions":"bugsnag\\.com|datadoghq\\.com"}',
  },
};

function getDynatraceScriptName(env: keyof typeof ENVS) {
  if (env === ENVS.QA) {
    return 'qaAgent';
  }

  if (env === ENVS.STAGING) {
    return 'stagingAgent';
  }

  if (env === ENVS.PRODUCTION) {
    return 'productionAgent';
  }
  return undefined;
}

const env = getEnv();

// The disable override is only allowed in QA (see isDynatraceOverrideAllowed) —
// short-circuiting here ensures isDynatraceDisabled() is never evaluated in
// higher environments. Persist the query param once at bootstrap so the
// override survives same-tab navigations without any write on a later read.
if (isDynatraceOverrideAllowed(env)) {
  persistDynatraceOverride();
}

const isDisabled = isDynatraceOverrideAllowed(env) && isDynatraceDisabled();

if (!isDisabled) {
  const scriptName = getDynatraceScriptName(env);
  const scriptAttributes = SCRIPT_ATTRIBUTES_PER_ENV[env];
  if (scriptName && scriptAttributes) {
    const scriptElement = document.createElement('script');

    // Set all attributes dynamically
    Object.entries(scriptAttributes).forEach(([key, value]) => {
      scriptElement.setAttribute(key, value);
    });

    scriptElement.id = 'dynatrace-agent';
    scriptElement.src = `${__webpack_public_path__}vendors/dynatraceAgents/${scriptName}.js`;
    scriptElement.onload = () => {
      registerAddEventModifier();
    };
    scriptElement.onerror = () => {
      console.error('Failed to load Dynatrace agent script');
    };

    document.head.appendChild(scriptElement);
  }
}
